/**
 * EKAA Admin RBAC - Roles and Permissions
 */

// ============== ROLES ==============
export enum Role {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  MODERATOR = 'moderator',
  SUPPORT = 'support',
  FINANCE = 'finance',
  VIEWER = 'viewer',
}

// Role hierarchy (higher index = more permissions)
export const ROLE_HIERARCHY: Role[] = [
  Role.VIEWER,
  Role.SUPPORT,
  Role.FINANCE,
  Role.MODERATOR,
  Role.ADMIN,
  Role.SUPER_ADMIN,
];

// ============== RESOURCES ==============
export enum Resource {
  DASHBOARD = 'dashboard',
  USERS = 'users',
  CARDS = 'cards',
  PLANS = 'plans',
  SUBSCRIPTIONS = 'subscriptions',
  PAYMENTS = 'payments',
  REWARDS = 'rewards',
  SUPPORT_TICKETS = 'support_tickets',
  SETTINGS = 'settings',
  AUDIT_LOGS = 'audit_logs',
}

// ============== ACTIONS ==============
export enum Action {
  LIST = 'list',
  SHOW = 'show',
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  EXPORT = 'export',
  ARCHIVE = 'archive',
  BAN = 'ban',
  REFUND = 'refund',
}

// ============== PERMISSION TYPE ==============
export interface Permission {
  resource: Resource;
  action: Action;
}

// ============== ROLE PERMISSIONS ==============
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [Role.SUPER_ADMIN]: [
    // Super Admin has ALL permissions
    ...Object.values(Resource).flatMap((resource) =>
      Object.values(Action).map((action) => ({ resource, action }))
    ),
  ],

  [Role.ADMIN]: [
    // Dashboard
    { resource: Resource.DASHBOARD, action: Action.LIST },
    // Users - full access except delete
    { resource: Resource.USERS, action: Action.LIST },
    { resource: Resource.USERS, action: Action.SHOW },
    { resource: Resource.USERS, action: Action.EDIT },
    { resource: Resource.USERS, action: Action.EXPORT },
    { resource: Resource.USERS, action: Action.BAN },
    // Cards - full access
    { resource: Resource.CARDS, action: Action.LIST },
    { resource: Resource.CARDS, action: Action.SHOW },
    { resource: Resource.CARDS, action: Action.CREATE },
    { resource: Resource.CARDS, action: Action.EDIT },
    { resource: Resource.CARDS, action: Action.DELETE },
    { resource: Resource.CARDS, action: Action.EXPORT },
    { resource: Resource.CARDS, action: Action.ARCHIVE },
    // Plans - full access
    { resource: Resource.PLANS, action: Action.LIST },
    { resource: Resource.PLANS, action: Action.SHOW },
    { resource: Resource.PLANS, action: Action.CREATE },
    { resource: Resource.PLANS, action: Action.EDIT },
    // Subscriptions - read + edit
    { resource: Resource.SUBSCRIPTIONS, action: Action.LIST },
    { resource: Resource.SUBSCRIPTIONS, action: Action.SHOW },
    { resource: Resource.SUBSCRIPTIONS, action: Action.EDIT },
    // Payments - read + refund
    { resource: Resource.PAYMENTS, action: Action.LIST },
    { resource: Resource.PAYMENTS, action: Action.SHOW },
    { resource: Resource.PAYMENTS, action: Action.REFUND },
    // Rewards - full access
    { resource: Resource.REWARDS, action: Action.LIST },
    { resource: Resource.REWARDS, action: Action.SHOW },
    { resource: Resource.REWARDS, action: Action.CREATE },
    { resource: Resource.REWARDS, action: Action.EDIT },
    // Support - read
    { resource: Resource.SUPPORT_TICKETS, action: Action.LIST },
    { resource: Resource.SUPPORT_TICKETS, action: Action.SHOW },
    // Settings - read + edit
    { resource: Resource.SETTINGS, action: Action.LIST },
    { resource: Resource.SETTINGS, action: Action.EDIT },
    // Audit logs - read
    { resource: Resource.AUDIT_LOGS, action: Action.LIST },
  ],

  [Role.MODERATOR]: [
    // Dashboard
    { resource: Resource.DASHBOARD, action: Action.LIST },
    // Users - read + ban
    { resource: Resource.USERS, action: Action.LIST },
    { resource: Resource.USERS, action: Action.SHOW },
    { resource: Resource.USERS, action: Action.BAN },
    // Cards - read + archive
    { resource: Resource.CARDS, action: Action.LIST },
    { resource: Resource.CARDS, action: Action.SHOW },
    { resource: Resource.CARDS, action: Action.ARCHIVE },
    // Support - full access
    { resource: Resource.SUPPORT_TICKETS, action: Action.LIST },
    { resource: Resource.SUPPORT_TICKETS, action: Action.SHOW },
    { resource: Resource.SUPPORT_TICKETS, action: Action.EDIT },
  ],

  [Role.SUPPORT]: [
    // Dashboard
    { resource: Resource.DASHBOARD, action: Action.LIST },
    // Users - read only
    { resource: Resource.USERS, action: Action.LIST },
    { resource: Resource.USERS, action: Action.SHOW },
    // Cards - read only
    { resource: Resource.CARDS, action: Action.LIST },
    { resource: Resource.CARDS, action: Action.SHOW },
    // Subscriptions - read only
    { resource: Resource.SUBSCRIPTIONS, action: Action.LIST },
    { resource: Resource.SUBSCRIPTIONS, action: Action.SHOW },
    // Support - full access
    { resource: Resource.SUPPORT_TICKETS, action: Action.LIST },
    { resource: Resource.SUPPORT_TICKETS, action: Action.SHOW },
    { resource: Resource.SUPPORT_TICKETS, action: Action.CREATE },
    { resource: Resource.SUPPORT_TICKETS, action: Action.EDIT },
  ],

  [Role.FINANCE]: [
    // Dashboard
    { resource: Resource.DASHBOARD, action: Action.LIST },
    // Users - read only
    { resource: Resource.USERS, action: Action.LIST },
    { resource: Resource.USERS, action: Action.SHOW },
    // Plans - read only
    { resource: Resource.PLANS, action: Action.LIST },
    { resource: Resource.PLANS, action: Action.SHOW },
    // Subscriptions - read + export
    { resource: Resource.SUBSCRIPTIONS, action: Action.LIST },
    { resource: Resource.SUBSCRIPTIONS, action: Action.SHOW },
    { resource: Resource.SUBSCRIPTIONS, action: Action.EXPORT },
    // Payments - full access
    { resource: Resource.PAYMENTS, action: Action.LIST },
    { resource: Resource.PAYMENTS, action: Action.SHOW },
    { resource: Resource.PAYMENTS, action: Action.EXPORT },
    { resource: Resource.PAYMENTS, action: Action.REFUND },
  ],

  [Role.VIEWER]: [
    // Dashboard only
    { resource: Resource.DASHBOARD, action: Action.LIST },
    // Users - read only
    { resource: Resource.USERS, action: Action.LIST },
    { resource: Resource.USERS, action: Action.SHOW },
    // Cards - read only
    { resource: Resource.CARDS, action: Action.LIST },
    { resource: Resource.CARDS, action: Action.SHOW },
  ],
};

// ============== HELPER FUNCTIONS ==============

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: Role, resource: Resource, action: Action): boolean {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.some((p) => p.resource === resource && p.action === action);
}

/**
 * Check if a role has any permission for a resource
 */
export function hasResourceAccess(role: Role, resource: Resource): boolean {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.some((p) => p.resource === resource);
}

/**
 * Get all permissions for a role
 */
export function getPermissionsForRole(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Get role level (for hierarchy comparison)
 */
export function getRoleLevel(role: Role): number {
  return ROLE_HIERARCHY.indexOf(role);
}

/**
 * Check if role1 has higher or equal level than role2
 */
export function isRoleAtLeast(role: Role, minimumRole: Role): boolean {
  return getRoleLevel(role) >= getRoleLevel(minimumRole);
}

/**
 * Map Keycloak roles to admin roles
 * Supports both formats: 'ekaa-super-admin' and 'super_admin'
 */
export function mapKeycloakRole(keycloakRoles: string[]): Role {
  // Check from highest to lowest
  if (
    keycloakRoles.includes('ekaa-super-admin') ||
    keycloakRoles.includes('super_admin') ||
    keycloakRoles.includes('realm-admin')
  ) {
    return Role.SUPER_ADMIN;
  }
  if (keycloakRoles.includes('ekaa-admin') || keycloakRoles.includes('admin')) {
    return Role.ADMIN;
  }
  if (keycloakRoles.includes('ekaa-moderator') || keycloakRoles.includes('moderator')) {
    return Role.MODERATOR;
  }
  if (keycloakRoles.includes('ekaa-support') || keycloakRoles.includes('support')) {
    return Role.SUPPORT;
  }
  if (keycloakRoles.includes('ekaa-finance') || keycloakRoles.includes('finance')) {
    return Role.FINANCE;
  }
  if (keycloakRoles.includes('ekaa-viewer') || keycloakRoles.includes('viewer')) {
    return Role.VIEWER;
  }
  // Default to viewer
  return Role.VIEWER;
}
